import { CSSProperties, forwardRef, memo, ReactNode, Ref } from 'react';
import { Box, Column, Icon, Pressable, Row, Text } from '~/commonComponents';
import { TIconNames } from '~/commonComponents/Icon/Icon';
import { Colors, TColorKeys } from '~/themes/colors';

export interface IInfoSectionProps {
  /** Icon name to display on the left */
  icon?: TIconNames;
  /** Title text */
  title: string;
  /** Show required indicator */
  required?: boolean;
  /** Optional icon color */
  iconColor?: TColorKeys | string;
  /** Optional icon size */
  iconSize?: number;
  /** Optional title color */
  titleColor?: TColorKeys | string;
  /** Optional title font size */
  titleFontSize?: number;
  /** Optional right action button */
  rightAction?: ReactNode;
  /** Optional callback when right action is clicked */
  onRightActionPress?: () => void;
  /** Optional right action icon name */
  rightActionIcon?: TIconNames;
  /** Optional right action icon color */
  rightActionIconColor?: TColorKeys | string;
  /** Optional right action icon size */
  rightActionIconSize?: number;
  /** Optional children content */
  children?: ReactNode;
  /** Optional container style */
  containerStyle?: CSSProperties;
  /** Optional data-cy for testing */
  dataCy?: string;
  /** Optional data-testid for testing */
  dataTestId?: string;
}

const Component = (
  {
    icon,
    title,
    required = false,
    iconColor = 'secondary_500',
    iconSize = 24,
    titleColor = 'primary_500',
    titleFontSize = 18,
    rightAction,
    onRightActionPress,
    rightActionIcon,
    rightActionIconColor = 'secondary_500',
    rightActionIconSize = 24,
    children,
    containerStyle,
    dataCy,
    dataTestId,
  }: IInfoSectionProps,
  ref: Ref<HTMLDivElement>,
) => {
  const renderRightAction = () => {
    if (rightAction) {
      return rightAction;
    }

    if (rightActionIcon && onRightActionPress) {
      return (
        <Pressable onPress={onRightActionPress}>
          <Icon name={rightActionIcon} color={rightActionIconColor} size={rightActionIconSize} />
        </Pressable>
      );
    }

    return null;
  };

  return (
    <Box
      ref={ref}
      paddingTop={24}
      paddingBottom={26}
      paddingLeft={66}
      paddingRight={66}
      border={`1.5px solid ${Colors.neutral_100}`}
      borderRadius={8}
      data-cy={dataCy}
      data-testid={dataTestId}
      style={containerStyle}
    >
      <Row alignItems="center" justifyContent="space-between" gap={16}>
        <Row alignItems="center" gap={8}>
          {icon && <Icon name={icon} color={iconColor} size={iconSize} />}
          <Text color={titleColor} fontSize={titleFontSize} bold>
            {title}
            {required && (
              <Text color="neutral_500">
                {' (Required)'}
                <Text color="error_500">*</Text>
              </Text>
            )}
          </Text>
        </Row>
        {renderRightAction()}
      </Row>
      {children && (
        <Column marginLeft={icon ? (iconSize ?? 0) + 9 : 0} marginTop={20} marginRight={28}>
          {children}
        </Column>
      )}
    </Box>
  );
};

export const InfoSection = memo(forwardRef(Component));
